import { useCallback, useState } from "react";
import { cssX, Dropdown, FCX, Portal, useUuid } from "@datassential/platform-ui-lib";
import { toast } from "react-toastify";
import cn from "classnames";
import { IconDownloadOutline, IconPpt } from "@icons";
import useComponentVisible from "@hooks/useComponentVisible";
import "./CategoryProfileSectionTitle.styles.scss";
import { CgSpinner } from "react-icons/cg";

export interface DownloadOption {
    id: number;
    title: string;
}

interface Props {
    title: string;
    onDownload?: () => Promise<void> | void;
    downloadOptions?: DownloadOption[];
    onDownloadOption?: (id: number) => Promise<void> | void;
}

const CategoryProfileSectionTitle: FCX<Props> = ({
    title,
    onDownload,
    downloadOptions,
    onDownloadOption,
    className,
    style,
    testId,
}) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const dropdownId = useUuid();

    const hasDropdown = !!onDownloadOption && !!downloadOptions && downloadOptions.length > 1;

    const runDownload = useCallback(async (download: () => Promise<void> | void) => {
        if (isDownloading) return;
        setIsDownloading(true);
        toast.info('Downloading...');
        try {
            await download();
            toast.success('Report downloaded');
        } catch {
            toast.error('Download failed');
        } finally {
            setIsDownloading(false);
        }
    }, [isDownloading]);

    const handleIconClick = useCallback(() => {
        if (hasDropdown) {
            setIsComponentVisible(prev => !prev);
            return;
        }
        if (onDownload) runDownload(onDownload);
    }, [hasDropdown, onDownload, runDownload, setIsComponentVisible]);

    const handleOptionClick = useCallback((id: number) => {
        setIsComponentVisible(false);
        if (onDownloadOption) runDownload(() => onDownloadOption(id));
    }, [onDownloadOption, runDownload, setIsComponentVisible]);

    return (
        <div
            className={cn("CategoryProfileSectionTitle", className)}
            style={style}
            data-testid={testId}
        >
            <h2 className="CategoryProfileSectionTitle__title">
                {title}
            </h2>
            <div className="CategoryProfileSectionTitle__line"/>
            {onDownload && (
                <>
                    <div className="CategoryProfileSectionTitle__divider"/>
                    <div className="CategoryProfileSectionTitle__download-wrapper" ref={ref}>
                        {!isDownloading && (
                            <IconDownloadOutline
                                className="CategoryProfileSectionTitle__download"
                                width={24}
                                height={24}
                                onClick={handleIconClick}
                                data-tooltip-id={hasDropdown ? dropdownId : undefined}
                                data-testid="buttonCategoryProfileSectionDownload"
                            />
                        )}
                        {isDownloading && (
                            <CgSpinner
                                className={cn(
                                    "CategoryProfileSectionTitle__download",
                                    "CategoryProfileSectionTitle__download--downloading",
                                )}
                                width={30}
                                height={30}
                                data-testid="buttonCategoryProfileSectionDownloadLoader"
                            />
                        )}
                        {hasDropdown && (
                            <Portal>
                                <Dropdown
                                    id={dropdownId}
                                    isOpened={isComponentVisible}
                                    testId="blockCategoryProfileSectionDownloadDropdown"
                                    groups={[{
                                        id: "download-options",
                                        items: downloadOptions!.map(option => ({
                                            id: option.id,
                                            content: (
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        cssX("Dropdown__item"),
                                                        "CategoryProfileSectionTitle__dropdown-item",
                                                    )}
                                                    onClick={() => handleOptionClick(option.id)}
                                                    data-testid={`buttonCategoryProfileSectionDownloadOption-${option.id}`}
                                                >
                                                    <IconPpt
                                                        className="CategoryProfileSectionTitle__dropdown-icon"
                                                        width={16}
                                                        height={20}
                                                    />
                                                    <span className="CategoryProfileSectionTitle__dropdown-label">
                                                    {option.title}
                                                </span>
                                                </button>
                                            ),
                                        })),
                                    }]}
                                />
                            </Portal>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default CategoryProfileSectionTitle;
